import {
    Box,
    Button,
    Checkbox,
    Textarea,
    chakra,
    useClipboard,
    useColorMode,
    useColorModeValue
} from '@chakra-ui/react';
import {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import useCustomToast from './ToastProvider';
import { commentBg } from '../Utils/commentBg';
import useStore from '../stores/store';
import layoutStore from '../stores/layoutStore';
import { AlarmIndicator, MotionBox } from './UiElements';
import { AnimatePresence, motion } from 'framer-motion';
import usePersistStore from '../stores/persistStore';
import { useItemSize } from '../Utils/useItemSize';
import useSelectStore from '../stores/selectStore';
import { isImage } from './AddSiteModal';

const ItemNote = ({
    groupId,
    id,
    currentColor,
    comment,
    isTodo,
    doneStatus,
    justAdded,
    hasAlarm,
    edited,
    editor,
    type,
    hoverNote,
    hasUrl,
    overlayItem
}) => {
    const deleteNote = useStore((state) => state.deleteNote);
    const submitNote = useStore((state) => state.submitNote);
    const setNoteEditMode = layoutStore((state) => state.setNoteEditMode);
    //const turnNoteIntoSite = (pastedData, prevValue, spaceId, tabId) => {}; //TODO action
    const roles = []; //TODO store state / current roles in workspace
    const [localEditState, setLocalEdit] = useState(false); //ATM only for hiding reminders icon on edit
    const groupIsLoading = useStore((state) => state.loadingGroups[groupId]);

    useEffect(() => {
        return () => {
            setNoteEditMode(null);
        };
    }, []);

    //Comment stuff
    const [currentComment, setCurrentComment] = useState(comment);
    useEffect(() => {
        setCurrentComment(comment);
    }, [comment]);

    useEffect(() => {
        calcNoteHeight();
    }, [currentComment]);

    const [commentColor, setCommentColorValue] = useState(currentColor);
    const setCommentColor = (newColor) => {
        setCommentColorValue(newColor);
        layoutStore.getState().setUpdateDockFromOutside({
            type: 'color',
            value: newColor
        });
    };
    useEffect(() => {
        setCommentColor(currentColor);
    }, [currentColor]);

    const [todo, setTodoValue] = useState(isTodo);
    const setTodo = (newStatus) => {
        setTodoValue(newStatus);
        layoutStore.getState().setUpdateDockFromOutside({
            type: 'todo',
            value: newStatus
        });
    };

    useEffect(() => {
        setTodo(isTodo);
    }, [isTodo]);

    let isDone = doneStatus ? doneStatus : false;
    const [isDoneLocal, setLocalDone] = useState(isDone);

    /* useEffect(() => {
        setLocalDone(doneStatus ? doneStatus : false);
    }, [doneStatus]); */

    const toggleStatus = useCallback(
        (e = null) => {
            e && e.stopPropagation();
            const newStatus = !isDoneLocal;
            setLocalDone(newStatus);
            useStore.getState().setDoneStatus(newStatus, groupId, id, hasAlarm);
        },
        [isDoneLocal, groupId, id, hasAlarm]
    );

    /* let isDone = doneStatus ? doneStatus : false;
    const [localStatus, setLocal] = useState(isDone); */

    const focusNote = () => {
        if (overlayItem) {
            return;
        }
        addTextViaRef();
        setNoteEditMode(id);
        setLocalEdit(true);
        //useSelectStore.getState().clearSelection();
    };

    const unFocusNote = () => {
        setNoteEditMode(null);
        setLocalEdit(false);
        layoutStore.getState().setUpdateDockFromOutside(null);
    };

    const inputRef = useRef(null);

    const addTextViaRef = () => {
        // When the input is focused, update the callback in the Zustand store
        layoutStore.setState({
            updateInputValue: (emoji) => {
                if (inputRef.current) {
                    let input = inputRef.current as HTMLTextAreaElement;
                    const cursorPos = input.selectionStart;
                    const text = input.value;
                    const newVal =
                        text.slice(0, cursorPos) +
                        emoji +
                        text.slice(cursorPos);
                    input.value = newVal;
                    setCurrentComment(newVal);

                    // Set the cursor position to the end of the inserted emoji
                    const newCursorPos = cursorPos + emoji.length;
                    input.setSelectionRange(newCursorPos, newCursorPos);
                }
            }
        });
    };

    const handleKeyPress = (e) => {
        let newVal = e.target.value;
        const inputRefHTML = inputRef.current as HTMLInputElement;
        if (e.shiftKey && e.keyCode === 13) {
            return;
        }
        if (e.keyCode === 13) {
            e.preventDefault();
            if (justAdded && !currentComment) {
                return;
            } else if (currentComment?.length === 0) {
                deleteNote(groupId, id, justAdded);
                showToast({
                    title: 'Note removed',
                    subtitle: 'Empty note removed',
                    type: 'info'
                });
            } else {
                setNoteEditMode(null);
                setLocalEdit(false);
                submitNote(
                    groupId,
                    id,
                    {
                        comment: currentComment,
                        doneStatus: isDone,
                        justAdded: false,
                        ...(todo !== undefined && { todo }),
                        ...(hasAlarm !== undefined && { hasAlarm }),
                        ...(commentColor !== undefined && { commentColor })
                    },
                    true
                );
            }
            //Here we do a special unfocus sequense to prevent flash from NewNoteTapArea
            setLocalEdit(false);
            layoutStore.setState({ currentDock: null });
            layoutStore.getState().setUpdateDockFromOutside(null);
        }
        if (e.keyCode === 27) {
            e.target.blur();
            unFocusNote();
        }
        if (e.shiftKey && e.keyCode === 46) {
            unFocusNote();
            //const sendToDeleteToast = todo ? 'To-do' : 'Note';
            deleteNote(groupId, id, justAdded);
        }
        if (e.keyCode === 9) {
            e.preventDefault();
            //let newVal = e.target.value;
            const pos = inputRefHTML.selectionEnd;
            const newPos = inputRefHTML.selectionEnd + 3;
            let insert = '   ';
            let newString = [
                newVal.slice(0, pos),
                insert,
                newVal.slice(pos)
            ].join('');
            //newVal += "   ";
            setCurrentComment(newString);
            runAfterUpdate(() => {
                inputRefHTML.selectionStart = newPos;
                inputRefHTML.selectionEnd = newPos;
            });
        }
    };

    const onBlur = () => {
        unFocusNote();
        if (justAdded && !currentComment) {
            deleteNote(groupId, id, justAdded);
        } else if (currentComment?.length === 0) {
            deleteNote(groupId, id, justAdded);
            showToast({
                title: 'Note removed',
                subtitle: 'Empty note removed',
                type: 'info'
            });
        } else if (
            currentComment !== comment ||
            commentColor !== currentColor ||
            todo !== isTodo
        ) {
            //let trimmedString = value.trim();
            const noteData = {
                comment: currentComment,
                doneStatus: isDone,
                justAdded: false,
                ...(todo !== undefined && { todo }),
                ...(hasAlarm !== undefined && { hasAlarm }),
                ...(commentColor !== undefined && { commentColor })
            };
            submitNote(groupId, id, noteData, false);
        }
        unFocusNote();
    };

    const { fontSize, padding } = useItemSize();

    const [textAreaRows, setTextAreaRows] = useState(1);

    useLayoutEffect(() => {
        calcNoteHeight();
    }, [groupIsLoading]);

    /* useEffect(() => {
        calcNoteHeight();
    }, [inputRef.current?.value]); */

    const calcNoteHeight = () => {
        if (!inputRef.current) return;
        const textareaLineHeight = 21;
        const minRows = 1;
        const maxRows = 16;

        const previousRows = inputRef.current.rows;
        inputRef.current.rows = minRows; // reset number of rows in textarea

        const currentRows = ~~(
            inputRef.current.scrollHeight / textareaLineHeight
        );

        if (currentRows === previousRows) {
            inputRef.current.rows = currentRows;
        }

        if (currentRows >= maxRows) {
            inputRef.current.rows = maxRows;
            inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }

        let newRows = currentRows < maxRows ? currentRows : maxRows;
        setTextAreaRows(newRows);
    };

    // textArea onChange height + Bullet point logic (- to •)
    const runAfterUpdate = useRunAfterUpdate();

    const showToast = useCustomToast();

    const handleCommand = useCallback((command: string) => {
        const commandMap = {
            '/done': () => {
                setTodo(true);
                toggleStatus();
            },
            '/todo': () => setTodo(true),
            '/red': () => setCommentColor(0),
            '/orange': () => setCommentColor(1),
            '/green': () => setCommentColor(2)
        };
        return commandMap[command]?.();
    }, [setTodo, toggleStatus, setCommentColor]);

    const valueInput = useCallback((e) => {
        const inputRefHTML = inputRef.current as HTMLInputElement;
        const textareaLineHeight = 21;
        const minRows = 1;
        const maxRows = 16;

        let newVal = e.target.value.slice(0, 5000);

        if (newVal.length > 5000) {
            showToast({
                title: 'Maximum length exceeded',
                subtitle: 'Note truncated to 5000 characters',
                type: 'warning'
            });
        }

        const previousRows = e.target.rows;
        e.target.rows = minRows; // reset number of rows in textarea

        const currentRows = ~~(e.target.scrollHeight / textareaLineHeight);

        if (currentRows === previousRows) {
            e.target.rows = currentRows;
        }

        if (currentRows >= maxRows) {
            e.target.rows = maxRows;
            e.target.scrollTop = e.target.scrollHeight;
        }

        let newRows = currentRows < maxRows ? currentRows : maxRows;
        setTextAreaRows(newRows);

        let newPos =
            newVal.includes('->') || newVal.includes('<-')
                ? inputRefHTML.selectionEnd - 1
                : inputRefHTML.selectionEnd;

        const commandMap = {
            '/done': () => {
                setTodo(true);
                toggleStatus();
            },
            '/todo': () => setTodo(true),
            '/red': () => setCommentColor(0),
            '/orange': () => setCommentColor(1),
            '/green': () => setCommentColor(2)
        };

        Object.keys(commandMap).forEach((command) => {
            if (newVal.includes(command)) {
                commandMap[command]();
                newVal = newVal.replace(command, '');
            }
        });

        newVal = newVal.replace('->', '→').replace('<-', '←');

        if (newVal.slice(0, 2) === '- ') {
            newVal = newVal.replace('- ', '• ');
        }

        newVal = newVal.replace('\n- ', '\n• ');

        setCurrentComment(newVal);

        runAfterUpdate(() => {
            inputRefHTML.selectionStart = newPos;
            inputRefHTML.selectionEnd = newPos;
        });

        return;
    }, [handleCommand, showToast, runAfterUpdate, setCurrentComment, inputRef]);

    const turnIntoLink = (pastedData, prevValue, isImage) => {
        useStore
            .getState()
            .turnNoteIntoSite(pastedData, prevValue, groupId, id, isImage);
        unFocusNote();
    };

    const handleOnPaste = useCallback((e) => {
        let prevValue = e.target.value;
        //@ts-ignore
        let clipboardData = e.clipboardData || window.clipboardData;
        let pastedData = clipboardData.getData('Text');
        let expression =
            '/https?://(?:www.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^s]{2,}|www.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^s]{2,}|https?://(?:www.|(?!www))[a-zA-Z0-9]+.[^s]{2,}|www.[a-zA-Z0-9]+.[^s]{2,}';
        let regex = new RegExp(expression);
        if (pastedData.match(regex)) {
            if (isImage(pastedData)) {
                showToast({
                    title: "Looks like an image", 
                    type: 'infoButton',
                    subtitle: "Turn this note into an image",
                    resClick: () => turnIntoLink(pastedData, prevValue, true)
                });
            } else {
                showToast({
                    title: "Looks like a URL", 
                    type: 'infoButton',
                    subtitle: "Turn this note into a site",
                    resClick: () => turnIntoLink(pastedData, prevValue, false)
                });
            }
        }
    }, [turnIntoLink, showToast]);

    const focusBoxShadow =
        (commentColor !== false && commentColor) || commentColor === 0
            ? '0px 0px 0px 3px ' + commentBg[commentColor]?.bg
            : '0px 0px 0px 3px #63B3ED';

    const { colorMode } = useColorMode();
    const commentBgColor = useColorModeValue('gray.100', '#35393c');
    const commentCopiedTextBgColor = useColorModeValue('gray.100', '#2A2D30');
    const textComment = useColorModeValue('gray.700', '#FFFFFF');

    const r = '7px';

    const stopPropagation = (e) => {
        if (!e.shiftKey) {
            e.stopPropagation();
        }
    };

    const isValidColor =
        (commentColor !== false && commentColor) || commentColor === 0;

    const backgroundColor =
        commentColor !== false &&
        commentColor !== null &&
        commentColor !== undefined &&
        commentBg[commentColor] &&
        commentBg[commentColor].bg
            ? commentBg[commentColor].bg
            : type === 'textCopied'
            ? commentCopiedTextBgColor
            : commentBgColor;

    const textAreaStyles = {
        filter: colorMode === 'light' ? 'none' : 'brightness(.8) contrast(1.2)',
        textDecoration: todo && isDoneLocal ? 'line-through' : 'initial',
        fontStyle: type === 'textCopied' ? 'italic' : 'normal',
        whiteSpace: 'pre-line',
        resize: 'none',
        scrollbarGutter: 'stable',
        '&::selection': {
            backgroundColor: isValidColor
                ? commentBg[commentColor].iconColor
                : '#55A6FE',
            color: 'white'
        }
    };

    const tColor = isValidColor ? commentBg[commentColor].text : textComment;

    const tBg = isValidColor ? commentBg[commentColor].bg : 'transparent';

    const tClassName =
        colorMode === 'dark'
            ? hoverNote
                ? 'groupScrollAreaDarkHover'
                : 'groupScrollAreaDark'
            : hoverNote
            ? 'groupScrollAreaHover'
            : 'groupScrollArea';

    const tFocus = {
        filter: 'brightness(1.0125)',
        outline: 'none',
        boxShadow: focusBoxShadow,
        backgroundColor: isValidColor
            ? commentBg[commentColor].focus
            : 'transparent'
    };

    const tOpacity = todo && isDoneLocal ? '0.5' : '1';

    return (
        <>
            {todo && (
                <TodoSwitch
                    isDone={isDoneLocal}
                    commentColor={commentColor}
                    toggleStatus={toggleStatus}
                />
            )}
            <Box
                width="100%"
                bg={backgroundColor}
                rounded={r}
                //minHeight={'36px'}
                display="flex"
                filter="none"
            >
                <Textarea
                    filter="none"
                    cursor={overlayItem ? 'grabbing' : 'text'}
                    id={'note-' + id}
                    onMouseDown={stopPropagation}
                    lineHeight={'21px'}
                    border="none"
                    transition="0.16s"
                    _focus={tFocus}
                    value={currentComment}
                    //@ts-ignore
                    ref={inputRef}
                    _hover={{ filter: 'brightness(1.0125)' }}
                    rows={textAreaRows}
                    onKeyDown={handleKeyPress}
                    onPaste={handleOnPaste}
                    spellCheck="false"
                    //defaultValue={value}
                    onInput={valueInput}
                    //onkeyup={(e) => keyUpValue(e)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={() => {
                        focusNote();
                    }}
                    onBlur={onBlur}
                    autoFocus={justAdded ? true : false}
                    className={tClassName}
                    opacity={tOpacity}
                    rounded={r}
                    padding={padding}
                    w="100%"
                    maxHeight="604px"
                    wordBreak="break-word"
                    fontSize={fontSize}
                    fontWeight={500}
                    overflow={hoverNote ? 'auto' : 'hidden'}
                    color={tColor}
                    bg={tBg}
                    sx={textAreaStyles}
                />
            </Box>
            {hasAlarm && !hasUrl && !localEditState && (
                <AlarmIndicator
                    hasAlarm={hasAlarm}
                    type={todo ? 'todo' : 'note'}
                    hoverSite={hoverNote}
                />
            )}
        </>
    );
};

export default ItemNote;

function useRunAfterUpdate() {
    const afterPaintRef = useRef(null);
    useLayoutEffect(() => {
        if (afterPaintRef.current) {
            afterPaintRef.current();
            afterPaintRef.current = null;
        }
    });
    const runAfterUpdate = (fn) => (afterPaintRef.current = fn);
    return runAfterUpdate;
}

export const TodoSwitch = memo(
    ({
        isDone,
        commentColor,
        toggleStatus,
        mt = '6px'
    }: {
        isDone: boolean;
        commentColor: any;
        toggleStatus: () => void;
        mt?: string;
    }) => {
        const setBlockScaleUp = layoutStore((state) => state.setBlockScaleUp);
        const todoBorder = useColorModeValue('#B0B7C1', '#4A5568');
        let isThereColor =
            (commentColor !== false && commentColor) || commentColor === 0
                ? true
                : false;

        const { todoSize, todoRounded } = useItemSize();

        const preventParentScale = () => {
            setBlockScaleUp(true);
        };
        const resetEditId = () => {
            setBlockScaleUp(false);
        };

        return (
            <MotionBox
                outline="none"
                onMouseDown={preventParentScale}
                onClick={toggleStatus}
                onMouseUp={resetEditId}
                mr="8px"
                mt={mt}
                mb="auto"
                maxH={todoSize}
                h={todoSize}
                minW={todoSize}
                maxW={todoSize}
                p="0"
                rounded={todoRounded}
                cursor="pointer"
                display="flex"
                alignItems="center"
                opacity={isDone ? '1' : '0.675'}
                whileTap={{ scale: 0.9, opacity: 0.8 }}
                //_active={{ transform: 'scale(0.8625)' }}
                _hover={{
                    transform: 'scale(1.075)',
                    opacity: 1,
                    bg: isDone
                        ? isThereColor
                            ? commentBg[commentColor].iconLight
                            : 'blue.300'
                        : 'transparent'
                }}
                bg={
                    isDone
                        ? isThereColor
                            ? commentBg[commentColor].iconLight
                            : 'blue.300'
                        : 'transparent'
                }
                boxShadow={
                    'inset 0px 0px 0px 2px ' +
                    (isDone
                        ? isThereColor
                            ? commentBg[commentColor].bg
                            : '#90CDF4'
                        : todoBorder)
                }
            >
                <AnimatePresence initial={false}>
                    {isDone && (
                        <motion.svg
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.16 }}
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            style={{ height: '24px', width: '24px' }}
                        >
                            <motion.path
                                d="M7.5 12L10.5 15L16.5 9"
                                stroke="#FFF"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                initial={{
                                    strokeDasharray: 24,
                                    strokeDashoffset: 24
                                }}
                                animate={{ strokeDashoffset: 0 }}
                                transition={{ duration: 0.28 }}
                            ></motion.path>
                        </motion.svg>
                    )}
                </AnimatePresence>
            </MotionBox>
        );
    }
);
